import { Controller } from "@hotwired/stimulus"
import { gsap } from "gsap"
import { ScrollTrigger } from "gsap/ScrollTrigger"

gsap.registerPlugin(ScrollTrigger)

export default class extends Controller {
  static targets = [
    "section", "header", "glare",
    "pillar", "summary"
  ]

  connect() {
    document.fonts.ready.then(() => {
      requestAnimationFrame(() => this.setup())
    })
  }

  disconnect() {
    ScrollTrigger.getAll().forEach((t) => t.kill())
  }

  setup() {
    const isMobile = window.innerWidth < 640

    this.setupBgTransition()
    this.setupPillars(isMobile)
    this.setupSummary()
  }

  setupBgTransition() {
    if (!this.hasSectionTarget) return
    if (!this.hasHeaderTarget) return
    if (!this.pillarTargets.length) return

    const section = this.sectionTarget
    const header = this.headerTarget
    const title = header.querySelector("h2")
    const subtitle = header.querySelector("p")
    const firstPillar = this.pillarTargets[0]

    // Pin the header so it holds centered
    ScrollTrigger.create({
      trigger: header,
      start: "top top",
      end: "+=400",
      pin: true,
      pinSpacing: true
    })

    // Glare sweep across title during pin
    if (this.hasGlareTarget) {
      gsap.to(this.glareTarget, {
        backgroundPosition: "0% 0",
        ease: "power1.inOut",
        scrollTrigger: {
          trigger: header,
          start: "top top",
          end: "+=400",
          scrub: true
        }
      })
    }

    // Bg transition as first pillar approaches
    const triggerConfig = {
      trigger: firstPillar,
      start: "top 80%",
      end: "top 20%",
      scrub: true
    }

    gsap.to(section, {
      backgroundColor: "#000",
      scrollTrigger: triggerConfig
    })

    if (title) {
      gsap.to(title, {
        color: "#fff",
        scrollTrigger: { ...triggerConfig }
      })
    }

    if (subtitle) {
      gsap.to(subtitle, {
        color: "#6b7280",
        scrollTrigger: { ...triggerConfig }
      })
    }
  }

  setupSummary() {
    if (!this.hasSummaryTarget) return

    ScrollTrigger.create({
      trigger: this.summaryTarget,
      start: "top top",
      end: "+=400",
      pin: true,
      pinSpacing: true
    })
  }

  setupPillars(isMobile) {
    this.pillarTargets.forEach((pillar) => {
      const pinTarget = pillar.querySelector(
        "[data-role='pin-target']"
      )
      const heading = pillar.querySelector(
        "[data-role='heading']"
      )
      const subtitle = pillar.querySelector(
        "[data-role='subtitle']"
      )
      const features = pillar.querySelectorAll(
        "[data-role='feature']"
      )
      const dot = pillar.querySelector(
        "[data-role='dot']"
      )
      const dotMobile = pillar.querySelector(
        "[data-role='dot-mobile']"
      )
      if (!pinTarget || !heading || !subtitle) return
      if (!features.length) return

      // Measure heading natural size and container
      const containerW = pinTarget.offsetWidth
      const headingW = heading.offsetWidth
      const xCenter = (containerW - headingW) / 2

      const vh = window.innerHeight
      const headingH = heading.offsetHeight
      const yCenter = (vh - headingH) / 2

      // Start heading centered in viewport
      gsap.set(heading, { x: xCenter, y: yCenter })

      // Scroll distances
      const introScroll = isMobile ? 300 : 800
      const holdScroll = isMobile ? 150 : 300
      const featureScroll = isMobile ? 400 : 600
      const totalScroll = introScroll + holdScroll +
        (features.length * featureScroll)

      // Convert to timeline proportions
      const introPct = introScroll / totalScroll
      const holdPct = holdScroll / totalScroll
      const featPct = featureScroll / totalScroll

      const endSize = isMobile ? "2rem" : "3rem"

      // Single timeline pinning the entire section
      const tl = gsap.timeline({
        scrollTrigger: {
          trigger: pinTarget,
          start: "top top",
          end: `+=${totalScroll}`,
          pin: true,
          scrub: true,
          pinSpacing: true
        }
      })

      // Phase 1: Appreciation pause
      tl.to({}, { duration: introPct * 0.4 })

      // Phase 2: Heading shrinks and moves to
      // top-left
      tl.to(heading, {
        fontSize: endSize,
        x: 0,
        y: 0,
        marginBottom: isMobile ? "0.5rem" : "1rem",
        duration: introPct * 0.6,
        ease: "power2.inOut"
      })

      // Phase 3: Dot appears and pulses
      const activeDot = isMobile ? dotMobile : dot
      if (activeDot) {
        tl.to(activeDot, {
          autoAlpha: 1,
          duration: introPct * 0.1,
          ease: "power1.out",
          onStart: () => {
            activeDot.classList.add("animate-pulse")
          }
        })
      }

      // Phase 4: Subtitle fades in
      tl.to(subtitle, {
        autoAlpha: 1,
        duration: holdPct * 0.4,
        ease: "power1.out"
      })

      // Brief hold with subtitle visible
      tl.to({}, { duration: holdPct * 0.6 })

      // Phase 4: Features cycle
      features.forEach((feat, i) => {
        const isLast = i === features.length - 1

        tl.to(feat, {
          autoAlpha: 1,
          y: 0,
          duration: featPct * 0.25,
          ease: "power1.out"
        })

        tl.to({}, { duration: featPct * 0.5 })

        if (!isLast) {
          tl.to(feat, {
            autoAlpha: 0,
            y: -20,
            duration: featPct * 0.25,
            ease: "power1.in"
          })
        } else {
          tl.to({}, { duration: featPct * 0.25 })
        }
      })
    })
  }
}
